import { createReducer } from '@reduxjs/toolkit'
import { ProjectHierarchy, Template, TemplateDestroyPreview } from 'interfaces/api/templates.interfaces'
import { ValidationErrorsApi } from 'packages/api'
import { createBuilder } from 'packages/redux-toolkit'

import {
  createTemplate,
  getAllTemplates,
  getProjectHierarchies,
  getTemplate,
  getTemplateDestroyPreview,
  setSingleTemplate,
  setTemplateValidationErrors,
  updateTemplate,
  updateTemplateFields,
} from './actions'

interface TemplatesState {
  list: {
    loading: boolean
    items: Template[]
    count: number
  }
  single: {
    loading: boolean
    item: Template | null
  }
  validation: ValidationErrorsApi | null
  fields: {
    loadingSave: boolean
    isSuccess: boolean
    templateFields: number[]
  }
  flexHierarchies: {
    loading: boolean
    flexEnabled: boolean
    items: ProjectHierarchy[]
  }
  destroyPreview: {
    loading: boolean
    item: TemplateDestroyPreview | null
  }
}

const initialState: TemplatesState = {
  list: {
    loading: false,
    items: [],
    count: 0,
  },
  single: {
    loading: false,
    item: null,
  },
  validation: null,
  fields: {
    loadingSave: false,
    isSuccess: false,
    templateFields: [],
  },
  flexHierarchies: {
    loading: false,
    flexEnabled: false,
    items: [],
  },
  destroyPreview: {
    loading: false,
    item: null,
  },
}

export const templatesReducer = createReducer(initialState, (builder) => {
  createBuilder(builder)
    .addCase(getAllTemplates.pending, (state) => {
      state.list.loading = true
    })
    .addCase(getAllTemplates.rejected, (state) => {
      state.list.loading = false
    })
    .addCase(getAllTemplates.fulfilled, (state, { payload }) => {
      state.list.loading = false
      state.list.items = payload.results
      state.list.count = payload.count
    })

    .addCase(getTemplate.pending, (state) => {
      state.single.loading = true
    })
    .addCase(getTemplate.rejected, (state) => {
      state.single.loading = false
      state.single.item = null
    })
    .addCase([getTemplate.fulfilled, setSingleTemplate], (state, { payload }) => {
      state.single.loading = false
      state.single.item = payload
      state.fields.templateFields = payload ? payload.fields.map(({ id }) => id) : []
    })

    .addCase(setTemplateValidationErrors, (state, { payload }) => {
      state.validation = payload
    })

    .addCase(updateTemplateFields.pending, (state, { meta }) => {
      state.fields.templateFields = state.fields.templateFields.filter(
        (item) => !meta.arg.data.fieldsExcludeIds.includes(item),
      )
      state.fields.loadingSave = true
      state.fields.isSuccess = false
    })
    .addCase(updateTemplateFields.fulfilled, (state, { meta }) => {
      meta.arg.data.fieldsIncludeIds.forEach((item) => {
        if (!state.fields.templateFields.includes(item)) {
          state.fields.templateFields.push(item)
        }
      })
      state.fields.loadingSave = false
      state.fields.isSuccess = true
    })
    .addCase(updateTemplateFields.rejected, (state) => {
      state.fields.loadingSave = false
      state.fields.isSuccess = false
    })

    .addCase(getProjectHierarchies.pending, (state) => {
      state.flexHierarchies.loading = true
    })
    .addCase(getProjectHierarchies.rejected, (state) => {
      state.flexHierarchies.loading = false
      state.flexHierarchies.items = []
      state.flexHierarchies.flexEnabled = false
    })
    .addCase(getProjectHierarchies.fulfilled, (state, { payload }) => {
      state.flexHierarchies.loading = false
      state.flexHierarchies.items = payload.hierarchies
      state.flexHierarchies.flexEnabled = payload.flexEnabled
    })

    .addCase(getTemplateDestroyPreview.pending, (state) => {
      state.destroyPreview.loading = true
      state.destroyPreview.item = null
    })
    .addCase(getTemplateDestroyPreview.rejected, (state) => {
      state.destroyPreview.loading = false
    })
    .addCase(getTemplateDestroyPreview.fulfilled, (state, { payload }) => {
      state.destroyPreview.loading = false
      state.destroyPreview.item = payload
    })

    .addCase([createTemplate.pending, updateTemplate.pending], (state) => {
      state.single.loading = true
    })
    .addCase([createTemplate.rejected, updateTemplate.rejected], (state) => {
      state.single.loading = false
    })
    .addCase([createTemplate.fulfilled, updateTemplate.fulfilled], (state) => {
      state.single.loading = false
    })
})
